import './Login.css';

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../services/authService';
import { useTranslation } from '../context/LanguageContext';

// 저장된 로그인 계정 이메일. 없거나 깨져 있으면 null.
const storedEmail = () => {
    try {
        const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
        return raw ? (JSON.parse(raw).email || null) : null;
    } catch {
        return null;
    }
};

// 토큰과 사용자 정보를 양쪽 스토리지에서 모두 지운다.
const clearStoredAuth = () => {
    ['token', 'user', 'rememberMe'].forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    });
};

function Login() {
    const nav = useNavigate();
    const { t, language, setLanguage, languages } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // 자동 로그인 (페이지 마운트 시)
    // demo-smi.seoro.ai.kr 로 접속 시 자동로그인 비활성화
    useEffect(() => {
        const autoLogin = async () => {
            const hostname = window.location.hostname;

            // 데모 URL인 경우 자동 로그인 비활성화
            const isDemoMode = hostname.startsWith('demo-') || hostname.includes('demo.');
            if (isDemoMode) {
                console.log('데모 모드: 자동 로그인 비활성화');
                return;
            }

            // 도메인별 자동 로그인 계정 매핑
            let autoLoginEmail = 'admin@seoro.ai.kr';
            if (hostname.startsWith('smi-vn.') || hostname.includes('smi-vn.')) {
                autoLoginEmail = 'admin_vn@seoro.ai.kr';
            } else if (hostname.startsWith('smi-kr.') || hostname.includes('smi-kr.')) {
                autoLoginEmail = 'admin_kr@seoro.ai.kr';
            }

            // 이미 로그인되어 있는지 확인.
            // 남아 있는 토큰이 이 도메인의 계정이 아니면 버리고 다시 로그인한다.
            // 토큰은 도메인을 구분하지 않으므로, smi 에서 통합 계정으로 로그인한 브라우저로
            // smi-kr 에 들어오면 그대로 전 공장이 보이던 문제가 있었다.
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (token) {
                if (storedEmail() === autoLoginEmail) {
                    nav('/home');
                    return;
                }
                console.log('다른 계정의 토큰이 남아 있어 재로그인합니다:', storedEmail(), '→', autoLoginEmail);
                clearStoredAuth();
            }

            setLoading(true);

            try {
                await login(autoLoginEmail, 'admin123', true);

                // 약간의 딜레이 후 이동 (토큰 저장 시간 확보)
                setTimeout(() => {
                    nav('/home');
                }, 100);
            } catch (err) {
                console.error('자동 로그인 실패:', err);
                console.error('에러 상세:', err.response?.data || err.message);
                // 자동 로그인 실패 시 에러는 표시하지 않음 (수동 로그인 가능하도록)
                setLoading(false);
            }
        };

        autoLogin();
    }, [nav]);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password, rememberMe);
            

            // 홈으로 이동
            nav('/home');
        } catch (err) {
            console.error('로그인 실패:', err);

            if (err.response && err.response.data) {
                // ApiResponse 형식: { success, message, errorCode, ... }
                const errorData = err.response.data;
                setError(errorData.message || errorData.data?.message || t('login.loginFailed'));
            } else {
                setError(t('login.serverError'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = (e) => {
        e.preventDefault();
        alert(t('login.forgotPasswordAlert'));
    };

    const handleContactAdmin = (e) => {
        e.preventDefault();
        alert(t('login.contactAdminAlert'));
    };

    return (
        <div className="login-page">
            {/* 언어 선택기 (우측 상단) */}
            <div className="login-language-switcher">
                {languages.map(lang => (
                    <button
                        key={lang.code}
                        type="button"
                        className={`login-lang-btn ${language === lang.code ? 'active' : ''}`}
                        onClick={() => setLanguage(lang.code)}
                    >
                        {lang.label}
                    </button>
                ))}
            </div>

            <div className="login-card">
                {/* Header Section */}
                <div className="login-header">
                    <p className="login-subtitle-top">{t('login.subtitleTop')}</p>
                    <h1 className="login-title">{t('login.systemTitle')}</h1>
                    <div className="subtitle-divider"></div>
                    <p className="login-subtitle-kr">{t('login.adminLogin')}</p>
                </div>

                {/* Login Form */}
                <form className="login-form" onSubmit={handleLogin}>
                    {error && (
                        <div style={{
                            padding: '12px',
                            marginBottom: '16px',
                            backgroundColor: '#fee',
                            color: '#c33',
                            borderRadius: '8px',
                            fontSize: '14px'
                        }}>
                            {error}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="email" className="form-label">{t('login.email')}</label>
                        <div className="input-container">
                            <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <input
                                type="email"
                                id="email"
                                className="form-input"
                                placeholder="admin@seoro.ai.kr"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="password" className="form-label">{t('login.password')}</label>
                        <div className="input-container">
                            <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                                <circle cx="12" cy="16" r="1" fill="currentColor"/>
                                <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                className="form-input"
                                placeholder="••••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading}
                            />
                            {showPassword ? (
                                <svg className="password-toggle" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={togglePasswordVisibility}>
                                    <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            ) : (
                                <svg className="password-toggle" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={togglePasswordVisibility}>
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20C5.93 20 1 12 1 12A18.45 18.45 0 0 1 5.06 5.06L17.94 17.94Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4C18.07 4 23 12 23 12A18.5 18.5 0 0 1 20.1 19.76L9.9 4.24Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </div>
                    </div>

                    <div className="form-options">
                        <label className="checkbox-container">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                disabled={loading}
                            />
                            <span className="checkmark"></span>
                            {t('login.rememberMe')}
                        </label>
                        <a href="#" className="forgot-password" onClick={handleForgotPassword}>{t('login.forgotPassword')}</a>
                    </div>

                    <button
                        type="submit"
                        className="login-button"
                        disabled={loading}
                        style={{ opacity: loading ? 0.7 : 1 }}
                    >
                        {loading ? t('login.submitting') : t('login.submit')}
                    </button>
                </form>

                {/* Footer */}
                <div className="login-footer">
                    <p>{t('login.noAccount')} <a href="#" onClick={handleContactAdmin}>{t('login.contactAdmin')}</a></p>
                </div>
            </div>

            {/* Copyright */}
            <div className="copyright">
                <p>Copyright © Seoro Corp. All Rights Reserved.</p>
            </div>
        </div>
    )
}

export default Login;